import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    ArrowLeft, User, FileText, Car, CreditCard, Activity,
    Save, Loader2, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePickerController } from '@/components/ui/date-picker';
import { toast } from 'sonner';

import {
    useDriver,
    useUpdatePersonalDetails,
    useUpdateVehicleDetails,
    useUpdateBankingInfo,
    useApproveKyc,
    useRejectKyc,
    useKeepInViewKyc,
    useDownloadDriverDocument
} from '@/api-services/hooks/useDriver';
import { DriverDTO } from '@/api-services/hooks/adminTypes';
import KycStatusBadge from '@/components/drivers/KycStatusBadge';
import DocumentViewer from '@/components/drivers/DocumentViewer';
import KycActionsPanel from '@/components/drivers/KycActionsPanel';
import DocumentUploadField from '@/components/drivers/DocumentUploadField';

const DriverDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'overview';
    const driverId = parseInt(id || '0');

    // API Hooks
    const { data: driver, loading: isLoadingDriver, refetch } = useDriver(driverId, { enabled: !!driverId });

    const { mutate: updatePersonal, loading: isUpdatingPersonal } = useUpdatePersonalDetails();
    const { mutate: updateVehicle, loading: isUpdatingVehicle } = useUpdateVehicleDetails();
    const { mutate: updateBanking, loading: isUpdatingBanking } = useUpdateBankingInfo();

    const { mutateAsync: approveKyc, isPending: isApprovingKyc } = useApproveKyc();
    const { mutateAsync: rejectKyc, isPending: isRejectingKyc } = useRejectKyc();
    const { mutateAsync: keepInView, isPending: isKeepingInView } = useKeepInViewKyc();
    const { mutate: downloadDoc } = useDownloadDriverDocument();

    // Forms
    const personalForm = useForm();
    const vehicleForm = useForm();
    const bankingForm = useForm();

    // Populate forms when data loads
    useEffect(() => {
        if (driver) {
            personalForm.reset({
                fullName: driver.fullName,
                email: driver.email,
                phoneNumber: driver.phoneNumber,
                dateOfBirth: driver.dateOfBirth,
                gender: driver.gender,
                address: driver.address,
                preferredLanguage: driver.preferredLanguage,
            });

            vehicleForm.reset({
                vehicleType: driver.vehicleType,
                vehicleMake: driver.vehicleMake,
                vehicleModel: driver.vehicleModel,
                vehicleRegistrationNumber: driver.vehicleRegistrationNumber || driver.vehicleLicensePlate,
                vehicleColor: driver.vehicleColor,
                yearOfManufacture: driver.yearOfManufacture || driver.vehicleYear,
            });

            bankingForm.reset({
                bankAccountHolderName: driver.bankAccountHolderName,
                bankName: driver.bankName,
                accountNumber: driver.accountNumber || driver.bankAccountNumber,
                ifscCode: driver.ifscCode || driver.bankIfscCode,
                swiftCode: driver.swiftCode,
                paymentFrequency: driver.paymentFrequency,
            });
        }
    }, [driver, personalForm, vehicleForm, bankingForm]);

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    // Update Handlers
    const onPersonalSubmit = (data: any) => {
        if (!driver) return;
        const updateData: DriverDTO = { ...driver, ...data };

        updatePersonal({ id: driverId, data: updateData })
            .then(() => {
                toast.success('Personal details updated');
                refetch();
            })
            .catch((err) => toast.error(`Update failed: ${err.message}`));
    };

    const onVehicleSubmit = (data: any) => {
        if (!driver) return;
        // Note: This hook might expect specific fields, check implementation
        // Assuming it takes DriverDTO or subset
        updateVehicle({
            driverId,
            ...data,
            vehicleCategoryId: driver.vehicleCategoryId || 0 // Ensure required fields
        })
            .then(() => {
                toast.success('Vehicle details updated');
                refetch();
            })
            .catch((err) => toast.error(`Update failed: ${err.message}`));
    };

    const onBankingSubmit = (data: any) => {
        if (!driver) return;
        updateBanking({ driverId, ...data })
            .then(() => {
                toast.success('Banking details updated');
                refetch();
            })
            .catch((err) => toast.error(`Update failed: ${err.message}`));
    };

    // KYC Actions
    const handleApproveKyc = async (id: number) => {
        try {
            await approveKyc(id);
            toast.success('KYC Approved');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to approve: ${err.message}`);
        }
    };

    const handleRejectKyc = async (id: number, reason: string) => {
        try {
            await rejectKyc({ driverId: id, rejectionReason: reason });
            toast.success('KYC Rejected');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to reject: ${err.message}`);
        }
    };

    const handleKeepInView = async (id: number) => {
        try {
            await keepInView(id);
            toast.success('Marked as Keep in View');
            refetch();
        } catch (err: any) {
            toast.error(`Failed to update status: ${err.message}`);
        }
    };

    const handleDownload = (url: string, fileName: string) => {
        // If it's a full URL, just open it
        if (url.startsWith('http')) {
            window.open(url, '_blank');
            return;
        }
        // Otherwise use the download hook
        downloadDoc(url)
            .then((blob) => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = fileName;
                link.click();
            })
            .catch((err) => toast.error(`Download failed: ${err.message}`));
    };

    if (isLoadingDriver) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!driver) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-2xl font-bold text-destructive">Driver not found</h2>
                <Button onClick={() => navigate('/admin/drivers')} className="mt-4">
                    Back to Drivers
                </Button>
            </div>
        );
    }

    // Prepare documents for viewer
    const kycDocuments = [
        { label: 'Govt ID Front', url: driver.govtIdFrontUrl, fileName: 'govt-id-front' },
        { label: 'Govt ID Back', url: driver.govtIdBackUrl, fileName: 'govt-id-back' },
        { label: 'Driving License Front', url: driver.drivingLicenseFrontUrl, fileName: 'license-front' },
        { label: 'Driving License Back', url: driver.drivingLicenseBackUrl, fileName: 'license-back' },
        { label: 'Selfie with ID', url: driver.selfieWithIdUrl, fileName: 'selfie' },
    ].filter(d => d.url);

    const vehicleDocuments = [
        { label: 'Ownership Document', url: driver.ownershipDocUrl || driver.vehicleRegistrationCertUrl, fileName: 'ownership-doc' },
        { label: 'Insurance Document', url: driver.insuranceDocUrl || driver.vehicleInsuranceCertUrl, fileName: 'insurance-doc' },
        { label: 'Road Tax Document', url: driver.roadTaxDocUrl, fileName: 'road-tax-doc' },
    ].filter(d => d.url);

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/drivers')}
                        className="hover:bg-muted"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div className="flex items-center gap-3">
                        {driver.profilePhotoUrl ? (
                            <img
                                src={driver.profilePhotoUrl}
                                alt={driver.fullName}
                                className="w-12 h-12 rounded-full object-cover border-2 border-primary/10"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                <User className="w-6 h-6 text-muted-foreground" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{driver.fullName}</h1>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>{driver.phoneNumber}</span>
                                <span>•</span>
                                <span>{driver.email}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <KycStatusBadge status={driver.kycStatus || 'PENDING'} size="lg" />
                    <Badge variant={driver.isActive ? 'default' : 'secondary'}>
                        {driver.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 h-auto">
                    <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
                    <TabsTrigger value="personal" className="py-2">Personal</TabsTrigger>
                    <TabsTrigger value="kyc" className="py-2">KYC & Docs</TabsTrigger>
                    <TabsTrigger value="vehicle" className="py-2">Vehicle</TabsTrigger>
                    <TabsTrigger value="banking" className="py-2">Banking</TabsTrigger>
                    <TabsTrigger value="activity" className="py-2">Activity</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="md:col-span-2">
                            <CardHeader>
                                <CardTitle>Driver Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Full Name</p>
                                    <p className="font-medium">{driver.fullName}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Phone</p>
                                    <p className="font-medium">{driver.phoneNumber}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Email</p>
                                    <p className="font-medium">{driver.email || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Vehicle</p>
                                    <p className="font-medium">{driver.vehicleMake} {driver.vehicleModel} ({driver.vehicleRegistrationNumber})</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Rides</p>
                                    <p className="font-medium">{driver.totalRidesCompleted || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Rating</p>
                                    <p className="font-medium flex items-center">
                                        {driver.averageRating || 'N/A'} <span className="text-yellow-500 ml-1">★</span>
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Quick Actions</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('kyc')}>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Review KYC
                                </Button>
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('vehicle')}>
                                    <Car className="w-4 h-4 mr-2" />
                                    Update Vehicle
                                </Button>
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleTabChange('banking')}>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Update Banking
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Personal Tab */}
                <TabsContent value="personal">
                    <Card>
                        <CardHeader>
                            <CardTitle>Personal Information</CardTitle>
                            <CardDescription>Update driver's personal details</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form id="personal-form" onSubmit={personalForm.handleSubmit(onPersonalSubmit)} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Full Name</Label>
                                        <Input {...personalForm.register('fullName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone Number</Label>
                                        <Input {...personalForm.register('phoneNumber')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input {...personalForm.register('email')} />
                                    </div>
                                    <div className="space-y-2">
                                        <DatePickerController
                                            control={personalForm.control}
                                            name="dateOfBirth"
                                            label="Date of Birth"
                                            placeholder="Select date of birth"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Gender</Label>
                                        <Select
                                            defaultValue={driver.gender}
                                            onValueChange={(val) => personalForm.setValue('gender', val)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="MALE">Male</SelectItem>
                                                <SelectItem value="FEMALE">Female</SelectItem>
                                                <SelectItem value="OTHER">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label>Address</Label>
                                        <Input {...personalForm.register('address')} />
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" form="personal-form" disabled={isUpdatingPersonal}>
                                {isUpdatingPersonal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </CardFooter>
                    </Card>
                </TabsContent>

                {/* KYC Tab */}
                <TabsContent value="kyc">
                    <div className="space-y-6">
                        <KycActionsPanel
                            driverId={driverId}
                            currentStatus={driver.kycStatus || 'PENDING'}
                            onApprove={handleApproveKyc}
                            onReject={handleRejectKyc}
                            onKeepInView={handleKeepInView}
                        />

                        <Card>
                            <CardHeader>
                                <CardTitle>KYC Documents</CardTitle>
                                <CardDescription>
                                    ID Type: <span className="font-medium">{driver.govtIdType}</span> |
                                    ID Number: <span className="font-medium">{driver.govtIdNumber}</span>
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <DocumentViewer
                                    documents={kycDocuments}
                                    onDownload={handleDownload}
                                />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Vehicle Tab */}
                <TabsContent value="vehicle">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Vehicle Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form id="vehicle-form" onSubmit={vehicleForm.handleSubmit(onVehicleSubmit)} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Vehicle Type</Label>
                                            <Select
                                                defaultValue={driver.vehicleType}
                                                onValueChange={(val) => vehicleForm.setValue('vehicleType', val)}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="CAR">Car</SelectItem>
                                                    <SelectItem value="BIKE">Bike</SelectItem>
                                                    <SelectItem value="AUTO">Auto</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Make</Label>
                                            <Input {...vehicleForm.register('vehicleMake')} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Model</Label>
                                            <Input {...vehicleForm.register('vehicleModel')} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Registration Number</Label>
                                            <Input {...vehicleForm.register('vehicleRegistrationNumber')} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Color</Label>
                                            <Input {...vehicleForm.register('vehicleColor')} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Year</Label>
                                            <Input type="number" {...vehicleForm.register('yearOfManufacture')} />
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                            <CardFooter>
                                <Button type="submit" form="vehicle-form" disabled={isUpdatingVehicle}>
                                    {isUpdatingVehicle && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Save Changes
                                </Button>
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Vehicle Documents</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DocumentViewer
                                    documents={vehicleDocuments}
                                    onDownload={handleDownload}
                                />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Banking Tab */}
                <TabsContent value="banking">
                    <Card>
                        <CardHeader>
                            <CardTitle>Banking Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form id="banking-form" onSubmit={bankingForm.handleSubmit(onBankingSubmit)} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Account Holder Name</Label>
                                        <Input {...bankingForm.register('bankAccountHolderName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bank Name</Label>
                                        <Input {...bankingForm.register('bankName')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Number</Label>
                                        <Input {...bankingForm.register('accountNumber')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>IFSC Code</Label>
                                        <Input {...bankingForm.register('ifscCode')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>SWIFT Code</Label>
                                        <Input {...bankingForm.register('swiftCode')} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Payment Frequency</Label>
                                        <Select
                                            defaultValue={driver.paymentFrequency}
                                            onValueChange={(val) => bankingForm.setValue('paymentFrequency', val)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select frequency" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="WEEKLY">Weekly</SelectItem>
                                                <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                                                <SelectItem value="MONTHLY">Monthly</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" form="banking-form" disabled={isUpdatingBanking}>
                                {isUpdatingBanking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </CardFooter>
                    </Card>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity">
                    <Card>
                        <CardHeader>
                            <CardTitle>Device & App Info</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Device Type</p>
                                <p className="font-medium">{driver.deviceType || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">OS Version</p>
                                <p className="font-medium">{driver.osVersion || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">App Version</p>
                                <p className="font-medium">{driver.appVersion || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Last Login</p>
                                <p className="font-medium">{driver.lastLogin || 'N/A'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-sm text-muted-foreground">FCM Token</p>
                                <p className="font-mono text-xs truncate bg-muted p-2 rounded mt-1">
                                    {driver.fcmToken || 'N/A'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default DriverDetailsPage;
